import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  API,
  BookingFullDTO,
  CompanyFullDTO,
  ContentCullDTO,
  Order,
  PublisherBookingDTO,
  ss_nm_Booking_retrieveAll_order,
} from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { SbLineChartModel } from '@ngx-seatback/charts';
import { NavigationService } from '@ngx-seatback/utils';
import { TranslateService } from '@ngx-translate/core';
import { BookingService } from '../../../services/booking.service';
import { ErrorService } from '../../../services/error.service';
import { MsOptions, OrderOption, TableWrapperOptions } from '../../../services/global/global-interfaces';
import { UserService } from '../../../services/user.service';
import { PermissionService } from '../../../services/permission.service';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { MediumUploadDTO } from '../../media/medium-explorer/medium-explorer.component';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';

export interface BookingReportingDTO extends PublisherBookingDTO {
  reporting?: SbLineChartModel;
}

@Component({
  selector: 'sb-page-network-bookings-list',
  templateUrl: './network-bookings-list.component.html',
  styleUrls: ['./network-bookings-list.component.scss'],
  standalone: false,
})
export class NetworkBookingsListComponent implements OnInit, ModalNavigationComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);
  isInModalContext: boolean;

  bookings: BookingReportingDTO[];
  loading: boolean;
  selectedCompanies: CompanyFullDTO[];
  companies: CompanyFullDTO[];

  options: TableWrapperOptions;

  msCompanyOptions: MsOptions;

  orderOption: OrderOption<string>;
  showDeprecatedBookings: boolean;
  showExcludedBookings: boolean;
  selectedBookings: BookingFullDTO[];
  contentCulls: ContentCullDTO[];
  filteredContentCulls: ContentCullDTO[];
  msContentCullOptions: MsOptions;
  debounceTimer: ReturnType<typeof setTimeout>;

  constructor(
    public readonly api: API,
    public readonly util: UtilService,
    public readonly navService: NavigationService,
    private readonly modalNavigation: ModalNavigationService,
    public readonly translate: TranslateService,
    public readonly bookingService: BookingService,
    public readonly errorHandler: ErrorService,
    public readonly userService: UserService,
    public readonly permissionService: PermissionService
  ) {
    this.contentCulls = [];
    this.filteredContentCulls = [];
    this.loading = true;
    this.bookings = [];
    this.debounceTimer = null;
    this.selectedCompanies = [];
    this.companies = [];
    this.resetOrderOption();
    this.showDeprecatedBookings = true;
    this.showExcludedBookings = true;
    this.selectedBookings = [];
  }

  ngOnInit(): void {
    this.userService.selectedCompany.subscribe((company: CompanyFullDTO) => {
      if (company) {
        const found = this.selectedCompanies.findIndex(value => value.id === company.id);
        if (found === -1) {
          this.selectedCompanies = [...this.selectedCompanies, company]; // trigger change detection
          this.requestDebounced();
        }
      }
    });
    if (this.isInModalContext) {
      const modalData = this.modalNavigation.getCurrentNavigation().data;
      if (modalData.bookings) {
        this.selectedBookings = modalData.bookings;
      }
    }
  }

  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption = { value: 'Id', direction: Order.desc };
  }

  requestDebounced(options?: TableWrapperOptions) {
    if (options) {
      this.options = options;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    this.loading = true;
    if (this.options?.pagination) {
      this.api
        .ss_nm_Booking_retrieveAll(
          {
            excluded: this.showExcludedBookings ? null : false,
            deprecated: this.showDeprecatedBookings ? null : false,
            contentCull: this.filteredContentCulls.length > 0 ? this.util.arrayToIdArray(this.filteredContentCulls) : null,
            customer_id:
              this.selectedCompanies && this.selectedCompanies.length > 0 ? this.util.arrayToIdArray(this.selectedCompanies) : [],
            search: this.stringTransformation.encodeSearchQueryParam(this.options?.exactMatch, this.options?.search),
            order: this.getOrder(),
          },
          this.options?.pagination
        )
        .pipe(
          take(1),
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (receivedBookings: PublisherBookingDTO[]) => {
            // check if pulsechart is needed
            this.bookings = receivedBookings;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  setToOrder(pValue: typeof this.orderOption.value) {
    if (!pValue) {
      // nothing is set fallback to default
      this.resetOrderOption();
    }
    if (this.orderOption.value === pValue) {
      if (this.orderOption.direction === Order.desc) {
        this.orderOption.direction = Order.asc;
      } else {
        this.resetOrderOption();
      }
    } else {
      this.orderOption = { value: pValue, direction: Order.desc };
    }

    this.requestDebounced();
  }

  getOrder(): { by: ss_nm_Booking_retrieveAll_order; direction: Order } {
    // if no orderOption -> return default order
    if (!this.orderOption.value) {
      this.resetOrderOption();
      return {
        by: ss_nm_Booking_retrieveAll_order.ID,
        direction: Order.asc,
      };
    }
    // if value equals column title
    switch (this.orderOption.value) {
      case 'Id':
        return { by: ss_nm_Booking_retrieveAll_order.ID, direction: this.orderOption.direction };
      case 'Advertiser':
        return { by: ss_nm_Booking_retrieveAll_order.ADVERTISER_NAME, direction: this.orderOption.direction };
      case 'Medium':
        return { by: ss_nm_Booking_retrieveAll_order.MEDIUM_NAME, direction: this.orderOption.direction };
      case 'Channel':
        return { by: ss_nm_Booking_retrieveAll_order.CHANNEL_NAME, direction: this.orderOption.direction };
    }
  }

  noExcludedBookings() {
    return this.translate.instant('lang.requirementBookingAssignment.noExcludedBookings');
  }

  noDeprecatedBookings() {
    return this.translate.instant('lang.requirementBookingAssignment.noDeprecatedBookings');
  }

  update($event, type: string) {
    if (type === 'COMPANIES') {
      if ($event && $event.length > 0) {
        this.selectedCompanies = $event;
      } else {
        this.selectedCompanies = [];
      }
    }
    if (type === 'CONTENT_CULLS') {
      if ($event && $event.length > 0) {
        this.filteredContentCulls = $event;
      } else {
        this.filteredContentCulls = [];
      }
    }
    this.refreshFromServer();
  }

  msRequest(type: string, options?: MsOptions) {
    if (type === 'COMPANIES') {
      if (options) {
        this.msCompanyOptions = options;
      }
      if (this.msCompanyOptions?.pagination) {
        this.api
          .shared_Company_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(false, this.msCompanyOptions?.search),
              hasCampaigns: true,
            },
            this.msCompanyOptions?.pagination
          )
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (comps: CompanyFullDTO[]) => {
              this.companies = comps;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
    if (type === 'CONTENT_CULLS') {
      if (options) {
        this.msContentCullOptions = options;
      }
      if (this.msContentCullOptions?.pagination) {
        this.api
          .cs_Cull_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(false, this.msContentCullOptions?.search),
              type: 'ContentCull',
            },
            this.msContentCullOptions?.pagination
          )
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (culls: ContentCullDTO[]) => {
              this.contentCulls = culls;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }

  isInSelectedBookings(medium: MediumUploadDTO): boolean {
    return this.selectedBookings.findIndex(x => x.id === medium.id) !== -1;
  }

  toggleBookingSelection(booking: BookingFullDTO) {
    const index = this.selectedBookings.findIndex(x => x.id === booking.id);
    if (index !== -1) {
      this.selectedBookings.splice(index, 1);
    } else {
      this.selectedBookings.push(booking);
    }
  }

  returnSelectedBookings() {
    this.modalNavigation.result(this.selectedBookings);
  }

  deselectBookings() {
    this.selectedBookings = [];
    this.returnSelectedBookings();
  }
}
